/**
 * Release Test Results Tab
 *
 * @module features/releases/components/ReleaseTestResultsTab
 * @description Reuses the workflow test-suite summary view for release runs,
 * with direct links to full execution/test-run detail pages.
 *
 * @see docs/api/services/release-qa.md
 */

import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, Flex, LoadingOrbit, Text } from '@chatsdet/ui';
import { ArrowsClockwise, ArrowSquareOut } from '@phosphor-icons/react';
import { useAgenticRunDetail } from '../../workflows/useAgenticRunDetail';
import { normalizeFromRest } from '../../workflows/useRunSummaryData';
import { runSummaryLockupCounts } from '../../workflows/runTotals';
import { RunTriageLockup } from '../../triage/RunTriageLockup';
import type { ReleaseRegressionConfig } from '../releaseTypes';
import { getReleaseRegressionScopeLabel } from '../releaseRegressionScope';
import { useEligibilityModel } from '../../../hooks/useEligibilityModel';

type ReleaseTestResultsTabProps = {
  executionId: string;
  regressionConfig?: ReleaseRegressionConfig | null;
};

export const ReleaseTestResultsTab: React.FC<ReleaseTestResultsTabProps> = ({
  executionId,
  regressionConfig,
}) => {
  const navigate = useNavigate();
  const eligibilityModel = useEligibilityModel();
  const runDetailQuery = useAgenticRunDetail(executionId);

  const execution = runDetailQuery.data?.execution;
  const testRuns = runDetailQuery.data?.testRuns ?? [];

  const runSummaryData = React.useMemo(() => {
    if (!execution) return null;
    return normalizeFromRest(execution, testRuns);
  }, [execution, testRuns]);

  if (runDetailQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  }

  if (runDetailQuery.isError || !execution || !runSummaryData) {
    return (
      <div className="text-center py-10 border-2 border-dashed border-border rounded-lg space-y-3">
        <Text className="text-muted-foreground">Unable to load workflow test execution details.</Text>
        <Button
          variant="outline"
          size="sm"
          onPress={() =>
            navigate({
              to: '/runs/test/$executionId',
              params: { executionId },
            })
          }
          iconLeft={<ArrowSquareOut size={14} />}
        >
          Open Regression Suite
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Flex
        align="center"
        justify="between"
        className="gap-3 rounded-md border border-border bg-muted/30 px-3 py-2"
      >
        <Flex align="center" gap="sm" className="min-w-0">
          <span className="text-muted-foreground shrink-0">
            <ArrowsClockwise size={16} />
          </span>
          <div className="min-w-0">
            <Text size="sm" weight="medium">
              Configured regression suite
            </Text>
            <Text size="xs" className="text-muted-foreground">
              {getReleaseRegressionScopeLabel(regressionConfig, eligibilityModel)} ·{' '}
              {execution.completedWorkflows}/{execution.totalWorkflows} workflows complete
            </Text>
          </div>
        </Flex>
        <Button
          variant="outline"
          size="sm"
          onPress={() =>
            navigate({
              to: '/runs/test/$executionId',
              params: { executionId },
            })
          }
          iconLeft={<ArrowSquareOut size={14} />}
          className="shrink-0"
        >
          Open Suite
        </Button>
      </Flex>

      <RunTriageLockup
        counts={runSummaryLockupCounts(runSummaryData)}
        items={runSummaryData.items}
        triageSubject={{ kind: 'execution', id: runSummaryData.executionId }}
        isRunInProgress={
          runSummaryData.status === 'running' || runSummaryData.status === 'queued'
        }
        finishedAt={runSummaryData.finishedAt}
        onItemSelect={(item) =>
          item.workflowRunId
            ? navigate({
                to: '/runs/$runId',
                params: { runId: item.workflowRunId },
                search: { testRunId: item.id },
              })
            : navigate({ to: '/runs/tests/$testRunId', params: { testRunId: item.id } })
        }
      />
    </div>
  );
};
